<template lang="html">
	<div class="omapi-settings-page omapi-settings-general">
		<slot name="tabs"></slot>

		<common-alerts id="om-settings-errors" :alerts="alerts"/>

		<common-card
			title="General"
		>
			<div class="omapi-card-setting-section">
				<header>
					<h3>
						License
					</h3>
				</header>
				<div>
					<div v-if="connected" class="your-plan">
						You're using <b>{{ userPlanName }}</b> - no license needed. Enjoy! 🙂
					</div>

					<div class="row-connect-buttons">
						<template v-if="!connected">
							<core-wizard-button v-show="!connectLoading" size="large">
								Create a Free Account to Get Started
							</core-wizard-button>
							<span v-show="!connectLoading" class="row-connect-buttons-or">
								or
							</span>
							<core-connect-button
								:outputErrors="false"
								type="primary"
								size="large"
								:refreshAfter="false"
								@onSuccess="afterConnect"
							>
								Connect an Existing Account
							</core-connect-button>
						</template>
						<template v-else>
							<core-alert
								v-show="!connectLoading"
								type="success"
								icon="success"
								class="omapi-settings-page__connected"
							>
								<div class="alert-message">
									Your account is connected!
								</div>
							</core-alert>
							<core-connect-button
								:outputErrors="false"
								type="primary"
								size="medium"
								:disconnect="true"
								class="omapi-settings-page__disconnect"
								:refreshAfter="false"
								@onSuccess="afterConnect"
							>
								<svg-close/> Disconnect
							</core-connect-button>
						</template>
					</div>

					<div v-if="showProUpsells" class="omapi-action-box">
						<header>
							<div>To unlock more features consider
								<core-upgrade-button
									utmMedium="GeneralSettings"
									class="omapi-link-green omapi-link-underline"
									:linkStyle="true"
								>upgrading to PRO.
								</core-upgrade-button>

							</div>
						</header>
						<div>
							As a valued {{ userPlanName }} user, you receive <span class="text-black font-bold">35% off</span> automatically applied at
							checkout!
						</div>
					</div>
				</div>
			</div>
			<div
				class="omapi-card-setting-section"
				v-if="isWooActive"
				:class="{'disabled': !connected}"
			>
				<header>
					<h3>
						WooCommerce
					</h3>
				</header>
				<form v-if="! isWooConnected" @submit.prevent="connectWoo">
					<p>
						In order to integrate WooCommerce with our Display Rules, OptinMonster needs <a :href="$urls.wpAdmin('?page=wc-settings&tab=advanced&section=keys')">WooCommerce REST API credentials</a> with Read Access Permissions.
					</p>

					<core-alert
						v-if="requestFail"
						:closes="true"
						type="error"
					>
						WooCommerce REST API keys could not be auto-generated on your behalf. Please try creating them manually and entering them here.
					</core-alert>

					<div v-if="showWooSettings" class="omapi-flex">
						<div class="omapi-flex-child omapi-input-wrap">
							<common-tooltip
								tooltip="Format: ck_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
							>
								<label>Consumer Key:</label>
							</common-tooltip>
							<input
								type="password"
								class="omapi-input"
								v-model="key"
								placeholder="Enter your consumer key here..."
							/>
						</div>
						<div class="omapi-flex-child omapi-input-wrap">
							<common-tooltip
								tooltip="Format: cs_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
							>
								<label>Consumer Secret:</label>
							</common-tooltip>

							<input
								type="password"
								class="omapi-input"
								v-model="secret"
								placeholder="Enter your consumer secret here..."
							/>
						</div>
					</div>

					<core-save-button
						size="large"
						:color="wooLoading ? '' : 'blue'"
						style="margin-bottom: 10px;"
						:disabled="wooLoading || !connected"
						:saving="wooLoading"
						:failed="requestFail"
					>
						{{ showWooSettings ? 'Connect WooCommerce' : 'Auto-Generate Keys + Connect WooCommerce' }}
					</core-save-button><span v-if="showWooSettings && ! wooLoading">or <a href="#" @click.prevent="hideWooSettingsAndAutogenerate">autogenerate and connect</a>.</span>

					<p v-if="!showWooSettings && ! wooLoading">
						or <a href="#" @click.prevent="toggleshowWooSettings">click here</a> to enter the the consumer key/secret pair manually.
					</p>

				</form>
				<form v-else @submit.prevent="disconnect">
					<core-alert
						type="success"
						icon="success"
						class="omapi-settings-page__woo-connected"
					>
						<div class="alert-message">
							WooCommerce is currently connected to OptinMonster{{ wooKey.id ? ' with the following key:' : '!'}}
						</div>
					</core-alert>
					<p v-if="wooKey.id">
						<code>{{ wooKey.description }} (&hellip;{{ wooKey.truncated }})</code>&nbsp;&nbsp;<a :href="wooKey.editUrl">View key</a>
					</p>
					<p>To remove your keys from OptinMonster, or to change the consumer key/secret pair associated with OptinMonster, you need to disconnect WooCommerce, below.</p>
					<core-save-button
						size="large"
						:color="wooLoading ? '' : 'blue'"
						style="margin-bottom: 10px;"
						:disabled="wooLoading || !connected"
						:saving="wooLoading"
						:failed="requestFail"
					>
						Disconnect WooCommerce
					</core-save-button>
				</form>
			</div>
		</common-card>

		<settings-pro-upsell v-if="showProUpsells"/>
	</div>
</template>

<script lang="js">
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
	data () {
		return {
			showWooSettings : false,
			requestFail     : false,
			key             : '',
			secret          : '',
		}
	},
	computed : {
		...mapState([ 'error', 'alerts' ]),
		...mapGetters([ 'connected', 'showProUpsells', 'userPlanName' ]),
		...mapGetters('wp', [ 'isWooActive', 'isWooConnected', 'wooKey' ]),
		wooLoading () {
			return this.$store.getters.isLoading('wooconnect')
		},
		connectLoading () {
			return this.$store.getters.isLoading([ 'connect', 'disconnect' ])
		},
	},
	watch : {
		isWooConnected (val) {
			this.requestFail = false
		},
		error (val) {
			if (!val) {
				this.requestFail = false
			}
		},
	},
	mounted () {
		if (!this.isWooActive) {
			return
		}

		this.autoFetchWooKey()
	},
	methods : {
		...mapActions('wp', [ 'autogenerateWooCreds', 'saveWooCreds', 'fetchWooKey', 'disconnectWoo' ]),
		toggleshowWooSettings () {
			this.showWooSettings = !this.showWooSettings
		},
		hideWooSettingsAndAutogenerate () {
			this.showWooSettings = false
			this.autogenerate()
		},
		connectWoo () {
			this.$store.commit('clearAlerts')
			this.requestFail = false
			return this.showWooSettings
				? this.connect()
				: this.autogenerate()
		},
		autogenerate () {
			this.requestFail = false

			this.autogenerateWooCreds()
				.catch(e => {
					this.showWooSettings = true
					this.requestFail = true
				})
		},
		connect () {
			this.requestFail = false
			this.saveWooCreds({
				key    : this.key,
				secret : this.secret,
			})
				.catch(e => {})
		},
		disconnect () {
			this.$store.commit('clearAlerts')
			this.requestFail = false
			this.disconnectWoo()
		},
		autoFetchWooKey () {
			if (!this.wooKey.id) {
				this.fetchWooKey().catch(e => {})
			}
		},
		afterConnect (disconnected) {
			this.$store.commit('loading', disconnected ? 'disconnect' : 'connect')
			this.$store.commit('clearAlerts')
			this.$store.commit('addAlert', {
				message : disconnected
					? 'Site successfully disconnected. Reloading, please wait...'
					: 'Site connected successfully! Reloading, please wait...',
				type : 'success',
			})
			setTimeout(() => window.location.reload(), 1500)
		},
	},
}
</script>
<style lang="scss">
	@import '../../assets/scss/variables';
	.omapi-screen .omapi-settings-general {
		.omapi-button:not(.loading-button) {
			&[disabled] {
				display: flex;
				justify-content: center;
				align-items: center;
				flex-direction: column;
				opacity: 1;
				> span {
					color: transparent;
					height: 0;
				}
			}
		}
	}
	.omapi-screen .omapi-settings-page {
		.omapi-button {
			.om-circle-loading {
				margin: 0;
			}
		}
		.omapi-settings-page__woo-connected.omapi-alert {
			padding: 6px 20px 5px;
			display: inline-flex;

			.alert-message {
				font-weight: $font_bold;
			}
		}
		.omapi-has-tooltip .popper {
			max-width: 1000px;
		}

		&__disconnect.omapi-button {
			display: inline-flex;
			justify-content: center;
			align-items: center;
			svg {
				width: 10px;
				height: 10px;
				margin-right: 3px;
			}
		}
	}
	.your-plan {
		margin: 0 0 20px;
	}
	.row-connect-buttons {
		align-content: center;
		align-items: center;
		display: flex;
		margin: 0 0 20px;
		@media screen and (max-width: 920px) {
			flex-wrap: wrap;
			align-items: flex-start;
			margin-bottom: 0;
		}

		.omapi-button {
			margin: 0 !important;
			@media screen and (max-width: 920px) {
				margin-bottom: 10px !important;
			}
		}

		.omapi-settings-page__connected.omapi-alert {
			padding: 6px 20px 5px;
			margin-right:  18px;
			margin-bottom: 0;
			@media screen and (max-width: 920px) {
				margin-bottom: 10px;
			}
			.alert-message {
				font-weight: $font_bold;
			}
		}

		.omapi-button.omapi-button__medium {
			font-size: 14px;
		}

		&-or {
			align-self: center;
			margin: 0 10px;
		}
	}
</style>
